/*
 * /ai-label — the page: «Поставить пометку» (mark) and «Проверить файл» (check) over one list
 * of files. Strings, presets and asset paths come from the server (window.__AIL).
 *
 *   inspect.js   what a file says now (classic script, window.AiLabelInspect — node-tested)
 *   c2pa-read.js C2PA validation, loaded only for a file that carries a manifest
 *   presets.js   what each jurisdiction asks for (data)
 *   image.js / video.js / audio.js — the visible and audible labels
 *   ai-mark.js   __aiMark.label(): the machine-readable label, merged into the file's metadata
 *
 * Marking is billed per FILE (tiers: guest 1 / Premium 1000 a day, a repeat of the same file the
 * same day is free); checking is diagnostics and never billed. Billing happens on DELIVERY.
 */
import { copyPreset, metaSpec, disclaimerLang, DISCLAIMER_LANGS, KINDS, declaredStamp, stampMarks } from './presets.js';
import { labelPicture, prepareLabel, stamp } from './image.js';
import { labelVideo, probeVideo, previewFrame, videoBitrateCap } from './video.js';
import { introPcm, wavLayout, wavWithIntro, mp3Layout, mp3WithIntro, ffmpegWithIntro } from './audio.js';

const SLUG = 'ai-label';
const W = window.__AIL || {};
const I18N = W.i18n || {};
const tr = (k, rep) => String(I18N[k] != null ? I18N[k] : k).replace(/:([a-z_]+)/g, (m, n) => (rep && rep[n] != null ? rep[n] : m));
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const ae = (f, p) => { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, f }, p || {})); } catch (e) { /* telemetry only */ } };
// The funnel and the «why it did not work» events (docs/why/primitives/result-door.md). Codes and
// counts only: never a file name, a prompt or the person's AI-system text.
const funnel = (stage, p) => { try { window.__aeFunnel && window.__aeFunnel(stage, Object.assign({ mode: S.mode }, p || {})); } catch (e) { /* telemetry only */ } };
const diag = (issue, p) => { try { window.__aeDiagnostic && window.__aeDiagnostic(Object.assign({ issue }, p || {})); } catch (e) { /* telemetry only */ } };
const errCode = (e) => String((e && (e.code || e.name)) || 'error').replace(/[^A-Za-z0-9_.-]/g, '').slice(0, 40);
const LOCALE = (window.__intlLocale && window.__intlLocale()) || 'en';
const fmtBytes = (n) => {
    const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let v = n;
    while (v >= 1024 && i < 3) { v /= 1024; i++; }
    try { return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: i ? 1 : 0 }).format(v) + ' ' + u[i]; } catch (e) { return v.toFixed(1) + ' ' + u[i]; }
};

const root = $('#ail');
const zone = $('#ailDrop');
const list = $('#ailFiles');
const runBtn = $('#ailRun');

/* ───────────── state ───────────── */

const S = {
    mode: 'mark',
    files: [],            // {id, file, kind, ext, report, verdict, c2pa, c2paState, result, error, previewUrl}
    preset: 'eu',
    plan: copyPreset('eu'),
    kind: 'generated',
    aiSystem: '',
    running: false,
    token: null
};
let seq = 0;

function settingsLoad() {
    const ts = window.__toolSettings;
    if (!ts) return;
    const saved = ts.load(SLUG, 'plan', null);
    if (saved && saved.preset && saved.plan) { S.preset = saved.preset; S.plan = Object.assign(copyPreset(saved.preset), saved.plan); }
    S.kind = ts.load(SLUG, 'kind', 'generated') === 'modified' ? 'modified' : 'generated';
    S.aiSystem = String(ts.load(SLUG, 'aiSystem', '') || '').slice(0, 120);
}
function settingsSave() {
    const ts = window.__toolSettings;
    if (!ts) return;
    try { ts.save(SLUG, 'plan', { preset: S.preset, plan: S.plan }); ts.save(SLUG, 'kind', S.kind); ts.save(SLUG, 'aiSystem', S.aiSystem); } catch (e) { /* storage full or off */ }
}

function kindOf(file) {
    const n = (file.name || '').toLowerCase(), t = file.type || '';
    if (/^image\//.test(t) || /\.(jpe?g|png|webp|heic|heif|avif|gif)$/.test(n)) return 'image';
    if (/^video\//.test(t) || /\.(mp4|mov|m4v|webm|mkv)$/.test(n)) return 'video';
    if (/^audio\//.test(t) || /\.(mp3|wav|m4a|aac|ogg|opus|flac)$/.test(n)) return 'audio';
    return 'other';
}
const extOf = (n) => { const m = /\.([a-z0-9]+)$/i.exec(n || ''); return m ? m[1].toLowerCase() : ''; };
const isHeic = (f) => /\.(heic|heif)$/i.test(f.name || '') || /heic|heif/.test(f.type || '');
// Pictures the marking lanes write (HEIC goes through JPEG). GIF and AVIF can be CHECKED — a GIF
// sticker from a generator, an AVIF from a phone — but no lane writes them.
const markablePicture = (f) => isHeic(f) || /^(jpe?g|png|webp)$/.test(extOf(f.name)) || /^image\/(jpeg|png|webp)$/.test(f.type || '');

/* ───────────── mode switch ───────────── */

function setMode(m) {
    S.mode = m === 'check' ? 'check' : 'mark';
    root.dataset.mode = S.mode;
    $$('[data-ail-mode]').forEach((b) => { const on = b.dataset.ailMode === S.mode; b.classList.toggle('on', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); });
    try { const u = new URL(location.href); if (S.mode === 'check') u.searchParams.set('mode', 'check'); else u.searchParams.delete('mode'); history.replaceState(null, '', u); } catch (e) { /* no history */ }
    renderAll();
    ae('mode', { m: S.mode });
}

/* ───────────── files in ───────────── */

function addFiles(files) {
    const add = Array.from(files || []).filter(Boolean);
    if (!add.length) return;
    for (const file of add) {
        const it = { id: ++seq, file, kind: kindOf(file), ext: extOf(file.name), report: null, verdict: null, c2pa: null, c2paState: 'none', result: null, error: null };
        it.markable = it.kind === 'audio' || it.kind === 'video' || (it.kind === 'image' && markablePicture(file));
        if (!it.markable && it.kind !== 'other') ae('unmarkable', { ext: it.ext });
        S.files.push(it);
        inspectOne(it);
    }
    root.classList.add('has-files');
    $('#ailWork').hidden = false;
    renderAll();
    precheck();
    ae('files_added', { n: add.length, kinds: Array.from(new Set(add.map(kindOf))).join(',') });
}

async function inspectOne(it, which) {
    const target = which === 'after' ? it.result.blob : it.file;
    try {
        const rep = await window.AiLabelInspect.inspect(target, it.file.name);
        if (which === 'after') { it.result.report = rep; it.result.verdict = window.AiLabelInspect.verdict(rep); }
        else { it.report = rep; it.verdict = window.AiLabelInspect.verdict(rep); }
        renderFile(it);
        if (rep.c2pa.present && which !== 'after') readC2paOf(it);
        else if (which !== 'after') reportChecked(it);
    } catch (e) {
        if (which !== 'after') { it.error = 'read'; renderFile(it); }
        diag('inspect_failed', { kind: it.kind, ext: it.ext, err: errCode(e) });
        window.__handleError && window.__handleError(e, 'ai_label_inspect', { say: tr('err_read') });
    }
}

/* What a checked file said — the families of marks found, never the values. Once per file. */
function reportChecked(it) {
    if (it.checkedSent || !it.verdict) return;
    it.checkedSent = true;
    const fam = new Set();
    it.verdict.items.forEach((i) => fam.add(String(i.code || '').replace(/^(dst|aigc|camera|it)_.*$/, '$1').slice(0, 24)));
    ae('checked', { kind: it.kind, ext: it.ext, level: it.verdict.level, c2pa: it.c2pa ? c2paKey(it.c2pa) : it.c2paState, src: Array.from(fam).sort().join(',').slice(0, 120), contra: it.verdict.contradictions.length });
    if (S.mode === 'check' && !S.checkFunnel) { S.checkFunnel = true; funnel('checked', { level: it.verdict.level }); if (window.__stage) window.__stage('checked', { kind: it.kind }); }
}

let c2paModP = null;
async function readC2paOf(it) {
    it.c2paState = 'loading';
    renderFile(it);
    try {
        c2paModP = c2paModP || import(W.c2paModule);
        const mod = await c2paModP;
        if (!mod.c2paSupported()) { it.c2paState = 'unsupported'; renderFile(it); return; }
        const mime = it.file.type || ({ jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', mp4: 'video/mp4', mov: 'video/quicktime', m4a: 'audio/mp4', mp3: 'audio/mpeg', wav: 'audio/wav', heic: 'image/heic', avif: 'image/avif' }[it.ext]);
        const c = await mod.readC2pa(it.file, mime);
        it.c2pa = c;
        it.c2paState = c ? 'done' : 'none';
        it.verdict = window.AiLabelInspect.verdict(it.report, c);
        ae('c2pa_read', { state: c ? c.state : 'none', trusted: c ? (c.trustedBy || '') : '' });
    } catch (e) {
        const msg = String((e && e.message) || e);
        // c2pa-rs names what it could not accept (UnknownAlgorithm, PrereleaseError, JUMBF/CBOR
        // parse…): that is a non-standard or broken manifest — «could not check in this browser»
        // sent people to try another browser for a file no browser verifies (no_alg.jpg,
        // prerelease.jpg of c2pa-rs; c2patool: «unknown algorithm», «Prerelease claim found»).
        const m = /C2pa\(([A-Za-z]+)\)/.exec(msg);
        it.c2paState = m && !/Wasm|Worker|Memory|Network|Fetch|Io$/i.test(m[1]) ? 'bad' : 'failed';
        it.c2paCode = m ? m[1].slice(0, 40) : '';
        ae('c2pa_failed', { err: msg.slice(0, 120), state: it.c2paState });
    }
    reportChecked(it);
    renderFile(it);
}

function removeFile(id) {
    S.files = S.files.filter((f) => f.id !== id);
    if (!S.files.length) { root.classList.remove('has-files'); $('#ailWork').hidden = true; }
    renderAll();
    precheck();
}

/* ───────────── rendering ───────────── */

function verdictHtml(it, v, c2pa, compact) {
    if (!v) return '<p class="ail-wait">' + esc(tr('reading')) + '</p>';
    const lvl = v.level;
    const title = tr('v_' + lvl + '_title');
    let h = '<div class="ail-verdict ail-v-' + lvl + '"><p class="ail-v-title">' + esc(title) + '</p>';
    if (!compact) h += '<p class="ail-v-sub">' + esc(tr('v_' + lvl + '_sub')) + '</p>';
    h += '</div>';
    if (compact) {
        const tags = [];
        if (v.ai.length || v.partial.length) tags.push(tr('tag_ai'));
        if (v.capture.length) tags.push(tr('tag_camera'));
        if (c2pa) tags.push(tr('tag_c2pa_' + c2paKey(c2pa)));
        else if (it.report && it.report.c2pa.present) tags.push(tr('tag_c2pa'));
        if (!tags.length && lvl === 'none') tags.push(tr('tag_none'));
        h += '<p class="ail-tags">' + tags.map((t) => '<span class="ail-tag">' + esc(t) + '</span>').join('') + '</p>';
    }
    return h;
}

const c2paKey = (c) => (c.state === 'Trusted' ? 'trusted' : c.state === 'Valid' ? (c.trustedBy === 'itl' ? 'itl' : 'untrusted') : 'invalid');

function itemText(i) {
    const rep = { value: i.value, app: i.app || '', tool: i.tool || '', key: i.key, producer: (i.aigc && i.aigc.ContentProducer) || '—' };
    const k = 'it_' + i.code;
    if (I18N[k] != null) return tr(k, rep);
    if (/^dst_/.test(i.code)) return tr('it_dst_other', rep);
    return i.key + ': ' + i.value;
}

function reportHtml(it) {
    const r = it.report, v = it.verdict;
    if (!r) return '';
    let h = verdictHtml(it, v, it.c2pa, false);
    if (v && v.timbrica) h += '<p class="tc-note ail-ours">' + esc(tr('note_ours', { tool: v.timbrica.tool })) + '</p>';
    if (v && v.declared) h += '<p class="tc-note">' + esc(tr('note_declared')) + '</p>';
    // C2PA
    if (r.c2pa.present) {
        h += '<div class="ail-c2pa"><p class="tc-eyebrow">' + esc(tr('c2pa_title')) + '</p>';
        if (it.c2paState === 'loading') h += '<p class="ail-wait">' + esc(tr('c2pa_loading')) + '</p>';
        else if (it.c2paState === 'bad') h += '<p class="tc-hint">' + esc(tr('c2pa_bad', { code: it.c2paCode || '?' })) + '</p>';
        else if (it.c2paState === 'failed' || it.c2paState === 'unsupported') h += '<p class="tc-hint">' + esc(tr('c2pa_' + it.c2paState)) + '</p>';
        else if (it.c2pa) {
            const c = it.c2pa, key = c2paKey(c);
            h += '<p class="ail-c2pa-state ail-c2pa-' + key + '">' + esc(tr('c2pa_' + key)) + '</p>';
            h += '<dl class="ail-dl">';
            if (c.issuer || c.commonName) h += '<dt>' + esc(tr('c2pa_signer')) + '</dt><dd>' + esc([c.commonName, c.issuer].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(' · ')) + '</dd>';
            if (c.generator) h += '<dt>' + esc(tr('c2pa_generator')) + '</dt><dd>' + esc(c.generator) + '</dd>';
            if (c.time) h += '<dt>' + esc(tr('c2pa_time')) + '</dt><dd>' + esc(fmtDate(c.time)) + '</dd>';
            c.actions.forEach((a) => {
                h += '<dt>' + esc(tr('c2pa_action')) + '</dt><dd>' + esc(actionText(a)) + '</dd>';
            });
            if (c.ingredients.length) h += '<dt>' + esc(tr('c2pa_ingredients')) + '</dt><dd>' + esc(c.ingredients.map((g) => g.title || '—').join(', ')) + '</dd>';
            const fails = c.failures.filter((f) => !(f === 'signingCredential.untrusted' && (key === 'untrusted' || key === 'itl')));
            if (fails.length) h += '<dt>' + esc(tr('c2pa_failures')) + '</dt><dd>' + fails.slice(0, 6).map((f) => esc(failText(f))).join('<br>') + '</dd>';
            h += '</dl>';
        }
        h += '</div>';
    }
    // what the file says, item by item
    const items = (v ? v.items : r.items).filter((i) => i.code !== 'c2pa_present' && i.source !== 'C2PA');
    if (items.length) {
        h += '<p class="tc-eyebrow ail-mt">' + esc(tr('found_title')) + '</p><ul class="ail-items">';
        items.forEach((i) => {
            h += '<li class="ail-it ail-k-' + esc(i.kind) + '"><span class="ail-it-t">' + esc(itemText(i)) + '</span><span class="ail-it-w">' + esc(i.source) + ' · ' + esc(i.key) + '</span>';
            if (/^(sd_parameters|generator_chunk|ai_prompt|midjourney_prompt)$/.test(i.code) && i.value) {
                h += '<details class="ail-raw"><summary>' + esc(tr('show_raw')) + '</summary><pre>' + esc(String(i.value).slice(0, 3000)) + '</pre></details>';
            }
            h += '</li>';
        });
        h += '</ul>';
    }
    if (v && v.contradictions.length) {
        h += '<div class="ail-contra"><p class="tc-eyebrow">' + esc(tr('contra_title')) + '</p>';
        v.contradictions.forEach((c) => { h += '<p class="tc-note">' + esc(tr('contra_' + c.code, { values: (c.values || []).join(', ') })) + '</p>'; });
        h += '</div>';
    }
    if (v && (v.level === 'none' || v.level === 'capture' || v.level === 'info')) h += '<p class="tc-note ail-mt">' + esc(tr('note_none')) + '</p>';
    if (v && v.google) h += '<p class="tc-note">' + esc(tr('note_synthid')) + '</p>';
    if (r.exif && Object.keys(r.exif).length && S.mode === 'check') {
        const ex = r.exif, rows = ['Make', 'Model', 'Software', 'DateTimeOriginal', 'Artist'].filter((k) => ex[k]);
        if (rows.length) h += '<details class="tc-drawer ail-mt"><summary>' + esc(tr('exif_title')) + '</summary><div class="tc-drawer-body"><dl class="ail-dl">' + rows.map((k) => '<dt>' + esc(k) + '</dt><dd>' + esc(ex[k]) + '</dd>').join('') + '</dl></div></details>';
    }
    if (S.mode === 'check' && v && it.c2paState !== 'loading') h += '<div class="tc-actions ail-mt"><button type="button" class="tc-btn sm ghost" data-ail-copy="' + it.id + '">' + esc(tr('copy_report')) + '</button></div>';
    return h;
}

function fmtDate(s) {
    try { return new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(s)); } catch (e) { return s; }
}

function actionText(a) {
    const act = tr('act_' + String(a.action).replace(/^c2pa\./, '').replace(/[^a-z_]/gi, '_'));
    const base = act.indexOf('act_') === 0 ? a.action : act;
    const parts = [base];
    if (a.dst) parts.push(tr('it_dst_' + a.dst, { value: a.dst }).indexOf('it_dst_') === 0 ? a.dst : tr('dstshort_' + a.dst, { value: a.dst }));
    if (a.agent) parts.push(a.agent);
    return parts.filter(Boolean).join(' · ');
}

function failText(code) {
    const k = 'c2paf_' + String(code).replace(/[^a-z0-9]/gi, '_');
    return I18N[k] != null ? tr(k) + ' (' + code + ')' : code;
}

function thumbHtml(it) {
    if (it.kind === 'image' && !isHeic(it.file)) {
        if (!it.thumbUrl) it.thumbUrl = URL.createObjectURL(it.file);
        return '<img class="ail-thumb" alt="" src="' + it.thumbUrl + '" decoding="async">';
    }
    if (it.heicThumbUrl) return '<img class="ail-thumb" alt="" src="' + it.heicThumbUrl + '" decoding="async">';
    const ico = it.kind === 'video' ? '🎬' : it.kind === 'audio' ? '🎧' : it.kind === 'image' ? '🖼️' : '📄';
    return '<span class="ail-thumb ail-thumb-ico" aria-hidden="true">' + ico + '</span>';
}

function resultHtml(it) {
    const r = it.result;
    if (!r) return '';
    let h = '<div class="ail-result">';
    if (r.error) {
        h += '<p class="tc-note ail-err">' + esc(r.error) + '</p></div>';
        return h;
    }
    if (r.verified) h += '<p class="ail-after-v">✓ ' + esc(tr('after_title', { what: tr('dstshort_' + KINDS[r.kind || S.kind].dst) })) + '</p>';
    h += '<p class="tc-eyebrow">' + esc(tr('written_title')) + '</p><table class="tc-table ail-diff"><thead><tr><th>' + esc(tr('col_where')) + '</th><th>' + esc(tr('col_what')) + '</th><th>' + esc(tr('col_before')) + '</th><th>' + esc(tr('col_after')) + '</th></tr></thead><tbody>';
    (r.rows || []).forEach((row) => {
        h += '<tr><td>' + esc(row.where) + '</td><td>' + esc(row.what) + '</td><td>' + esc(row.before || '—') + '</td><td>' + esc(row.after) + '</td></tr>';
    });
    h += '</tbody></table>';
    (r.notes || []).forEach((n) => { h += '<p class="tc-note">' + esc(n) + '</p>'; });
    if (r.verified === false) h += '<p class="tc-note ail-err">' + esc(tr('verify_failed')) + '</p>';
    else if (r.verified) h += '<p class="tc-hint ail-ok">' + esc(tr('verify_ok')) + '</p>';
    h += '<div class="tc-actions"><button type="button" class="tc-btn sm" data-ail-dl="' + it.id + '">⬇ ' + esc(tr('download_one', { name: r.name })) + '</button>'
        + '<span class="tc-hint">' + esc(fmtBytes(it.file.size)) + ' → ' + esc(fmtBytes(r.blob.size)) + '</span></div>';
    h += '</div>';
    return h;
}

function renderFile(it) {
    let card = list.querySelector('[data-ail-id="' + it.id + '"]');
    if (!card) {
        card = document.createElement('article');
        card.className = 'tc-panel ail-card';
        card.dataset.ailId = it.id;
        list.appendChild(card);
    }
    let h = '<div class="ail-card-head">' + thumbHtml(it) + '<div class="ail-card-name"><b>' + esc(it.file.name) + '</b><span class="tc-hint">' + esc(fmtBytes(it.file.size)) + ' · ' + esc(tr('kind_' + it.kind)) + '</span></div>'
        + '<button type="button" class="ail-x" data-ail-rm="' + it.id + '" aria-label="' + esc(tr('remove')) + '">×</button></div>';
    if (it.error === 'read') h += '<p class="tc-note ail-err">' + esc(tr('err_read')) + '</p>';
    else if (S.mode === 'check') h += reportHtml(it);
    else {
        // the picture (preview before, result after) and the words about it side by side when
        // the card is wide; one under the other on a phone
        const img = it.result && it.result.previewUrl ? '<img class="ail-after-img" alt="' + esc(tr('after_alt')) + '" src="' + it.result.previewUrl + '">' : '';
        const media = img || (it.preview && !it.result ? '<div class="ail-preview"></div>' : '');
        h += '<div class="ail-card-body' + (media ? '' : ' no-media') + '">' + (media ? '<div class="ail-card-media">' + media + '</div>' : '') + '<div class="ail-card-text">';
        h += verdictHtml(it, it.verdict, it.c2pa, true);
        if (it.kind === 'other') h += '<p class="tc-note">' + esc(tr('unsupported_file')) + '</p>';
        else if (!it.markable) h += '<p class="tc-note">' + esc(tr('unsupported_mark')) + '</p>';
        if (it.undecodable && S.plan.video.on && !it.result) h += '<p class="tc-note">' + esc(tr('warn_video_undecodable')) + '</p>';
        if (it.report && it.report.c2pa.present) h += '<p class="tc-note">' + esc(tr('warn_c2pa_edit')) + '</p>';
        if (it.verdict && (it.verdict.ai.length || it.verdict.partial.length) && !it.result) h += '<p class="tc-hint">' + esc(tr('already_marked')) + '</p>';
        h += '<details class="tc-drawer ail-mt"><summary>' + esc(tr('details')) + '</summary><div class="tc-drawer-body">' + reportHtml(it) + '</div></details>';
        h += resultHtml(it);
        h += '</div></div>';
    }
    card.innerHTML = h;
    if (it.preview && S.mode === 'mark' && !it.result) {
        const box = card.querySelector('.ail-preview');
        if (box) box.appendChild(it.preview);
    }
}

function renderAll() {
    list.innerHTML = '';
    S.files.forEach(renderFile);
    renderPlan();
    const total = S.files.filter((f) => f.markable).length;
    $('#ailCount').textContent = S.files.length ? tr('files_n', { n: S.files.length }) : '';
    runBtn.textContent = total > 1 ? tr('run_n', { n: total }) : tr('run');
    runBtn.disabled = S.running || !total;
    $('#ailDlAll').hidden = S.files.filter((f) => f.result && f.result.blob).length < 2;
    schedulePreview();
}

/* ───────────── the plan (rail) ───────────── */

function renderPlan() {
    const p = S.plan;
    $$('[data-ail-preset]').forEach((b) => { const on = b.dataset.ailPreset === S.preset; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#ailPresetNote').textContent = tr('preset_' + S.preset + '_note');
    $$('[data-ail-kind]').forEach((b) => { const on = b.dataset.ailKind === S.kind; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    const kinds = new Set(S.files.map((f) => f.kind));
    const any = S.files.length === 0;
    $('#ailPanelImage').hidden = !(any || kinds.has('image') || kinds.has('video'));
    $('#ailPanelAudio').hidden = !(any || kinds.has('audio'));
    $('#ailVideoWhen').hidden = !(any || kinds.has('video'));
    const vis = p.image;
    $('#ailVisOn').checked = !!vis.on;
    $('#ailVisBody').hidden = !vis.on;
    $('#ailStyle').value = vis.style;
    $('#ailText').hidden = vis.style !== 'text';
    $('#ailText').value = vis.text || '';
    $$('[data-ail-corner]').forEach((b) => { const on = b.dataset.ailCorner === vis.corner; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('[data-ail-size]').forEach((b) => { const on = b.dataset.ailSize === vis.size; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#ailTheme').value = vis.theme;
    $('#ailTranslucent').checked = !!vis.translucent;
    $('#ailTranslucentRow').hidden = vis.style !== 'eu';
    $('#ailWhen').value = p.video.when;
    $('#ailStartSec').value = p.video.startSec;
    $('#ailEverySec').value = p.video.everySec;
    $('#ailEveryRow').hidden = p.video.when !== 'interval';
    $('#ailStartRow').hidden = p.video.when === 'whole';
    const a = p.audio;
    $('#ailVoice').checked = !!a.voice;
    $('#ailLang').value = a.lang || 'auto';
    $('#ailLangRow').hidden = !a.voice;
    $('#ailMorse').checked = !!a.morse;
    $('#ailChime').checked = !!a.chime;
    $('#ailRepeat').value = String(a.repeatMin || 0);
    $('#ailAigc').checked = !!p.meta.aigc;
    $('#ailSystem').value = S.aiSystem;
}

function setPlan(mutator, what) {
    mutator(S.plan);
    if (what) ae('plan', { what, preset: S.preset });
    // the video label follows the picture label's look; its timing stays its own
    Object.assign(S.plan.video, { on: S.plan.image.on, style: S.plan.image.style, corner: S.plan.image.corner, size: S.plan.image.size, text: S.plan.image.text, translucent: S.plan.image.translucent });
    settingsSave();
    renderPlan();
    schedulePreview();
}

function bindRail() {
    $$('[data-ail-preset]').forEach((b) => b.addEventListener('click', () => {
        S.preset = b.dataset.ailPreset; S.plan = copyPreset(S.preset);
        settingsSave(); renderPlan(); schedulePreview(); ae('preset', { p: S.preset });
    }));
    $$('[data-ail-kind]').forEach((b) => b.addEventListener('click', () => { S.kind = b.dataset.ailKind; settingsSave(); renderPlan(); schedulePreview(); ae('kind', { k: S.kind }); }));
    $('#ailVisOn').addEventListener('change', (e) => setPlan((p) => { p.image.on = e.target.checked; }, 'vison'));
    $('#ailStyle').addEventListener('change', (e) => setPlan((p) => { p.image.style = e.target.value; }, 'style'));
    $('#ailText').addEventListener('input', (e) => setPlan((p) => { p.image.text = e.target.value.slice(0, 40); }));
    $$('[data-ail-corner]').forEach((b) => b.addEventListener('click', () => setPlan((p) => { p.image.corner = b.dataset.ailCorner; }, 'corner')));
    $$('[data-ail-size]').forEach((b) => b.addEventListener('click', () => setPlan((p) => { p.image.size = b.dataset.ailSize; }, 'size')));
    $('#ailTheme').addEventListener('change', (e) => setPlan((p) => { p.image.theme = e.target.value; p.video.theme = e.target.value === 'auto' ? 'dark' : e.target.value; }, 'theme'));
    $('#ailTranslucent').addEventListener('change', (e) => setPlan((p) => { p.image.translucent = e.target.checked; }, 'translucent'));
    $('#ailWhen').addEventListener('change', (e) => setPlan((p) => { p.video.when = e.target.value; }, 'when'));
    $('#ailStartSec').addEventListener('change', (e) => setPlan((p) => { p.video.startSec = Math.max(2, Math.min(600, Number(e.target.value) || 3)); }, 'startsec'));
    $('#ailEverySec').addEventListener('change', (e) => setPlan((p) => { p.video.everySec = Math.max(10, Math.min(3600, Number(e.target.value) || 60)); }, 'everysec'));
    $('#ailVoice').addEventListener('change', (e) => setPlan((p) => { p.audio.voice = e.target.checked; p.audio.on = p.audio.voice || p.audio.morse || p.audio.chime; }, 'voice'));
    $('#ailLang').addEventListener('change', (e) => setPlan((p) => { p.audio.lang = e.target.value; }, 'lang'));
    $('#ailMorse').addEventListener('change', (e) => setPlan((p) => { p.audio.morse = e.target.checked; p.audio.on = p.audio.voice || p.audio.morse || p.audio.chime; }, 'morse'));
    $('#ailChime').addEventListener('change', (e) => setPlan((p) => { p.audio.chime = e.target.checked; p.audio.on = p.audio.voice || p.audio.morse || p.audio.chime; }, 'chime'));
    $('#ailRepeat').addEventListener('change', (e) => setPlan((p) => { p.audio.repeatMin = Math.max(0, Math.min(60, Number(e.target.value) || 0)); }, 'repeat'));
    $('#ailAigc').addEventListener('change', (e) => setPlan((p) => { p.meta.aigc = e.target.checked; }, 'aigc'));
    $('#ailSystem').addEventListener('input', (e) => { S.aiSystem = e.target.value.slice(0, 120); settingsSave(); });
    $('#ailListen').addEventListener('click', listenIntro);
}

/* ───────────── previews ───────────── */

let previewTimer = 0;
function schedulePreview() {
    clearTimeout(previewTimer);
    if (S.mode !== 'mark') return;
    previewTimer = setTimeout(buildPreviews, 160);
}

async function buildPreviews() {
    for (const it of S.files) {
        if (it.result || !it.markable || (it.kind !== 'image' && it.kind !== 'video')) { it.preview = null; continue; }
        if (it.kind === 'video' && it.undecodable === undefined) {
            // once per file: can this browser show the picture at all? (HEVC MOV in Chromium
            // without the platform decoder reads as a video with a 0×0 picture)
            let info = null;
            try { info = await probeVideo(it.file); } catch (e) { info = null; }
            it.undecodable = !(info && info.width > 0 && info.height > 0);
            if (it.undecodable) ae('video_undecodable', { ext: it.ext });
            renderFile(it);
        }
        if (!S.plan.image.on || (it.kind === 'video' && it.undecodable)) { it.preview = null; renderFile(it); continue; }
        try {
            let c;
            if (it.kind === 'image') {
                const pic = isHeic(it.file) ? await heicJpegOf(it) : it.file;
                if (isHeic(it.file) && !it.heicThumbUrl) it.heicThumbUrl = URL.createObjectURL(pic);
                // WebKit before Safari 17 throws on 'from-image'; without the option its default is the same
                const bmp = await createImageBitmap(pic, { imageOrientation: 'from-image' }).catch(() => createImageBitmap(pic));
                const sc = Math.min(1, 560 / Math.max(bmp.width, bmp.height));
                c = document.createElement('canvas');
                c.width = Math.max(1, Math.round(bmp.width * sc)); c.height = Math.max(1, Math.round(bmp.height * sc));
                const ctx = c.getContext('2d', { willReadFrequently: true });
                ctx.drawImage(bmp, 0, 0, c.width, c.height);
                if (bmp.close) bmp.close();
                stamp(ctx, await prepareLabel(S.plan.image, S.kind, c.width, c.height, ctx));
            } else {
                const pf = await previewFrame(it.file, S.plan.video, S.kind, 1);
                const sc = Math.min(1, 560 / Math.max(pf.canvas.width, pf.canvas.height));
                c = document.createElement('canvas');
                c.width = Math.round(pf.canvas.width * sc); c.height = Math.round(pf.canvas.height * sc);
                c.getContext('2d').drawImage(pf.canvas, 0, 0, c.width, c.height);
            }
            c.className = 'ail-preview-c';
            c.setAttribute('role', 'img');
            c.setAttribute('aria-label', tr('preview_alt'));
            it.preview = c;
            if (!S.previewFunnel) { S.previewFunnel = true; funnel('preview_shown', { kind: it.kind }); }
        } catch (e) { it.preview = null; diag('preview_failed', { kind: it.kind, ext: it.ext, err: errCode(e) }); }
        renderFile(it);
    }
}

async function listenIntro() {
    const a = S.plan.audio;
    try {
        const lang = disclaimerLang(a.lang, LOCALE);
        const pcm = await introPcm(Object.assign({}, a, { voice: a.voice || (!a.morse && !a.chime) }), S.kind, lang, 48000, 1);
        if (!pcm) return;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        if (window.__liveAudioCtxReady) await window.__liveAudioCtxReady(ctx);
        const b = ctx.createBuffer(1, pcm[0].length, 48000);
        b.copyToChannel(pcm[0], 0);
        const s = ctx.createBufferSource(); s.buffer = b; s.connect(ctx.destination); s.start();
        s.onended = () => { try { ctx.close(); } catch (e) { /* closed */ } };
        ae('listen', { lang, morse: a.morse ? 1 : 0, chime: a.chime ? 1 : 0 });
    } catch (e) {
        window.__handleError && window.__handleError(e, 'ai_label_listen', { say: tr('err_listen') });
    }
}

/* ───────────── marking ───────────── */

const REFUSAL_KEYS = { heif_not_supported: 'ref_heif', id3_version: 'ref_id3', id3_unsync: 'ref_id3', id3_ext_header: 'ref_id3', id3_footer: 'ref_id3', xmp_too_large: 'ref_xmp_big',
    wav_rf64: 'ref_big', wav_too_large: 'ref_big', unsupported: 'ref_unsupported', already_labelled: 'ref_already', webm_unwalkable: 'ref_layout', mp4_unwalkable: 'ref_layout', jpeg_unwalkable: 'ref_layout', png_unwalkable: 'ref_layout', webp_unwalkable: 'ref_layout', wav_unwalkable: 'ref_layout', id3_unwalkable: 'ref_layout', xmp_unreadable: 'ref_layout' };

function baseName(n) { return String(n || 'file').replace(/\.[^.]+$/, ''); }

async function heicToJpeg(file) {
    const t0 = Date.now();
    try {
        if (!window.HeicConvert) throw Object.assign(new Error('HEIC engine missing'), { code: 'heic' });
        window.__ailHeicPool = window.__ailHeicPool || window.HeicConvert.create({ workerUrl: W.heicWorker, heicToUrl: W.heicTo });
        await window.__ailHeicPool.ready;
        const r = await window.__ailHeicPool.convert(file, { format: 'image/jpeg', quality: 0.95, keepExif: true });
        ae('heic_converted', { ms: Date.now() - t0, mb: Math.round(file.size / 1048576) });
        return new File([r.blob], baseName(file.name) + '.jpg', { type: 'image/jpeg' });
    } catch (e) {
        // The pool needs OffscreenCanvas in a worker (Safari before 16.4 has none). Then the
        // site's main-thread libheif, then the browser's own decoder (Safari reads HEIC itself);
        // the Exif does not survive these two — the picture and the label do.
        ae('heic_fallback', { err: errCode(e) });
        let blob = null;
        if (window.HeicDecode && window.HeicDecode.toBlob) { try { blob = await window.HeicDecode.toBlob(file, 'image/jpeg', 0.95); } catch (_) { blob = null; } }
        if (!blob) {
            const bmp = await createImageBitmap(file);
            const c = document.createElement('canvas');
            c.width = bmp.width; c.height = bmp.height;
            c.getContext('2d').drawImage(bmp, 0, 0);
            if (bmp.close) bmp.close();
            blob = await window.__canvasBlob(c, 'image/jpeg', 0.95);
        }
        return new File([blob], baseName(file.name) + '.jpg', { type: 'image/jpeg' });
    }
}
/* The zone hands an iPhone HEIC over AS IS (data-heic="keep"): the check reads the HEIC's own
   Exif/XMP, and the marking converts once — with Exif — instead of the zone's Exif-less JPEG
   (QA 23.10.2026: make, model and date were lost on the way). */
function heicJpegOf(it) {
    if (!it.heicP) { it.heicP = heicToJpeg(it.file); it.heicP.catch(() => { it.heicP = null; }); }
    return it.heicP;
}

async function markOne(it, onProgress) {
    const plan = S.plan, notes = [];
    let src = it.file, blob = null, ext = it.ext, visible = null, intro = null;
    const spec = metaSpec(plan, S.kind, S.aiSystem);
    const prior = stampMarks(((it.verdict && it.verdict.items) || []).filter((i) => i.code === 'timbrica_declared').map((i) => i.value));
    const marks = { visible: prior.visible.slice(), audible: prior.audible.slice() };
    const visKey = plan.image.style + '@' + plan.image.corner;
    // the same corner already carries a label this tool drew: a second one on top would leave the
    // first sticking out from under it — the metadata is renewed, the pixels are left alone
    const keepVisible = (it.kind === 'image' || it.kind === 'video') && plan.image.on && prior.visible.some((v) => v.split('@')[1] === plan.image.corner);
    const keepAudible = it.kind === 'audio' && prior.audible.length > 0;
    if (keepVisible) notes.push(tr('note_visible_kept'));
    if (keepAudible) notes.push(tr('note_audible_kept'));
    if (keepVisible || keepAudible) ae('remark_kept', { visible: keepVisible ? 1 : 0, audible: keepAudible ? 1 : 0 });
    if (it.kind === 'image' && isHeic(src)) { src = await heicJpegOf(it); ext = 'jpg'; notes.push(tr('note_heic')); }
    if (it.kind === 'video' && plan.video.on && !keepVisible && it.undecodable === undefined) {
        let pi = null;
        try { pi = await probeVideo(src); } catch (e) { pi = null; }
        it.undecodable = !(pi && pi.width > 0 && pi.height > 0);
    }
    if (it.kind === 'video' && plan.video.on && !keepVisible && it.undecodable) {
        notes.push(tr('note_video_undecodable'));
        diag('video_undecodable', { ext: it.ext });
    }
    // A lane that fails (no Web Audio, a canvas over the device's limit, both video lanes down)
    // does not cost the file its machine-readable label: the metadata is still written and the
    // result says what is missing — not a bare «could not mark» (QA 23.10.2026, WebKit).
    const laneFailed = (what, e) => {
        if (window.__isUserCancel && window.__isUserCancel(e)) throw e;
        notes.push(tr(what === 'audible' ? 'note_audible_failed' : 'note_visible_failed'));
        diag('lane_failed', { what, kind: it.kind, ext: it.ext, err: errCode(e) });
    };
    if (it.kind === 'image' && plan.image.on && !keepVisible) try {
        const out = await labelPicture(src, plan.image, S.kind);
        blob = out.blob;
        spec.baseXmp = out.carried.xmp || undefined;
        visible = out.label;
        if (out.quality && out.format === 'jpeg') notes.push(tr('note_jpeg_q', { q: out.quality }));
        if (out.carried.thumb) notes.push(tr('note_thumb'));
        if (out.carried.orientation && out.carried.orientation !== 1) notes.push(tr('note_orientation'));
        if (out.carried.hadC2pa) notes.push(tr('note_c2pa_kept'));
        if (out.format === 'png') notes.push(tr(out.carried.bits16 ? 'note_png16' : 'note_png'));
        if (plan.image.style === 'cn') notes.push(out.label.inkPx >= 0.05 * Math.min(out.width, out.height) - 0.5 ? tr('note_cn_ok', { px: out.label.inkPx, min: Math.ceil(0.05 * Math.min(out.width, out.height)) }) : tr('note_cn_small'));
        marks.visible.push(visKey);
    } catch (e) { laneFailed('visible', e); }
    else if (it.kind === 'video' && plan.video.on && !keepVisible && !it.undecodable) try {
        const info = await probeVideo(src);
        info.capBps = await videoBitrateCap(src, info);
        const out = await labelVideo(src, plan.video, S.kind, info, onProgress);
        blob = out.blob; ext = out.ext; visible = out.label;
        it.lane = out.lane;
        marks.visible.push(visKey);
        if (out.lane === 'ffmpeg') notes.push(tr('note_video_ffmpeg'));
        if (/webm|mkv/.test(it.ext) && ext === 'mp4') notes.push(tr('note_webm_mp4'));
        notes.push(tr('note_video_when_' + plan.video.when, { s: plan.video.startSec, every: plan.video.everySec }));
    } catch (e) { if (e && e.code === 'video_undecodable') { it.undecodable = true; notes.push(tr('note_video_undecodable')); } else laneFailed('visible', e); }
    else if (it.kind === 'audio' && plan.audio.on && !keepAudible && (plan.audio.voice || plan.audio.morse || plan.audio.chime) && !webAudioStub()) try {
        const lang = disclaimerLang(plan.audio.lang, LOCALE);
        const every = (plan.audio.repeatMin || 0) * 60;
        const done = await markAudio(src, plan.audio, lang, every, notes, onProgress);
        blob = done.blob; ext = done.ext; intro = done.intro;
        ['chime', 'voice', 'morse'].forEach((k) => { if (plan.audio[k] && !marks.audible.includes(k)) marks.audible.push(k); });
    } catch (e) { blob = null; ext = it.ext; intro = null; laneFailed('audible', e); }
    if (it.kind === 'audio' && plan.audio.on && !keepAudible && (plan.audio.voice || plan.audio.morse || plan.audio.chime) && webAudioStub()) {
        notes.push(tr('note_audible_failed'));
        diag('lane_failed', { what: 'audible', kind: it.kind, ext: it.ext, err: 'no_webaudio' });
    }
    const day = window.__localYmd ? window.__localYmd() : ((d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'))(new Date());
    spec.stamp = declaredStamp(S.kind, day, marks);
    const before = blob || src;
    const res = await window.__aiMark.label(before, it.file.name, spec);
    if (res.refused) { ae('mark_refused', { reason: String(res.refused).slice(0, 40), ext: it.ext, partial: blob ? 1 : 0 }); diag('mark_refused', { reason: String(res.refused).slice(0, 40), ext: it.ext }); }
    if (res.refused && !blob) throw Object.assign(new Error(res.refused), { userText: tr(REFUSAL_KEYS[res.refused] || 'ref_layout') });
    if (res.refused) notes.push(tr(REFUSAL_KEYS[res.refused] || 'ref_layout'));
    const name = baseName(it.file.name) + '-ai-label.' + (ext || it.ext || 'bin');
    return { blob: res.blob, name, writes: res.writes, notes, visible, intro, refused: res.refused };
}

/* No Web Audio here: timbrica-core.js put its stub in place of the constructor. */
function webAudioStub() {
    const C = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    return !C || C.isStub === true;
}

function introInfo(pcm, rate, a, lang, every) {
    const parts = [];
    if (a.chime) parts.push(tr('intro_chime'));
    if (a.voice) parts.push(tr('intro_voice', { lang: lang.toUpperCase() }));
    if (a.morse) parts.push(tr('intro_morse'));
    let what = parts.join(' + ');
    if (every > 0) what += ' ' + tr('intro_repeat', { min: Math.round(every / 60) });
    const sec = pcm && pcm[0] ? pcm[0].length / rate : 0;
    let s = sec.toFixed(1);
    try { s = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(sec); } catch (e) { /* plain */ }
    return { what, sec: s };
}

async function markAudio(file, a, lang, every, notes, onProgress) {
    const ext = extOf(file.name);
    const kind = S.kind;
    if (ext === 'wav') {
        const lay = await wavLayout(file);
        if (lay) {
            const intro = await introPcm(a, kind, lang, lay.fmt.rate, lay.fmt.channels);
            const rem = every > 0 ? intro : null;
            notes.push(tr('note_wav_lossless'));
            return { blob: await wavWithIntro(file, lay, intro, rem, every), ext: 'wav', intro: introInfo(intro, lay.fmt.rate, a, lang, every) };
        }
    }
    if (ext === 'mp3' && !(every > 0) && window.Mp3FrameTrim) {
        try {
            const lay = await mp3Layout(file, window.Mp3FrameTrim);
            if (lay && lay.mdb0) {
                const intro = await introPcm(a, kind, lang, lay.first.sampleRate, lay.first.channelMode === 3 ? 1 : 2);
                const out = await mp3WithIntro(file, lay, intro, window.Mp3FrameTrim);
                notes.push(tr('note_mp3_lossless'));
                return { blob: out, ext: 'mp3', intro: introInfo(intro, lay.first.sampleRate, a, lang, 0) };
            }
        } catch (e) {
            ae('mp3_lossless_failed', { err: String((e && (e.code || e.message)) || e).slice(0, 80) });
        }
    }
    // FFmpeg: decode, join, encode once
    let info = { rate: 44100, channels: 2, durationSec: 0, kbps: 160, ext: ext === 'mp3' ? 'mp3' : 'm4a' };
    try {
        if (window.MediaAudioProbe) {
            const pr = await window.MediaAudioProbe.probe(file);
            if (pr && pr.sampleRate) info.rate = pr.sampleRate;
            if (pr && pr.channels) info.channels = Math.min(2, pr.channels);
            if (pr && pr.durationSec) info.durationSec = pr.durationSec;
        }
        if (!info.durationSec) {
            const el = document.createElement('audio'); el.preload = 'metadata';
            const url = URL.createObjectURL(file); el.src = url;
            await new Promise((r) => { el.onloadedmetadata = r; el.onerror = r; setTimeout(r, 8000); });
            if (isFinite(el.duration)) info.durationSec = el.duration;
            URL.revokeObjectURL(url);
        }
        if (info.durationSec > 0) info.kbps = file.size * 8 / info.durationSec / 1000;
    } catch (e) { /* defaults */ }
    const intro = await introPcm(a, kind, lang, info.rate, info.channels);
    const out = await ffmpegWithIntro(file, intro, every > 0 ? intro : null, every, info, onProgress);
    // the number the file really has: FFmpeg's door applies the plan's ceiling to a whole-file encode
    let kbps = Math.round(Math.max(64, Math.min(320, info.kbps)));
    try { if (window.__qualityTier && window.__qualityTier.clamp) kbps = window.__qualityTier.clamp('kbps', kbps) || kbps; } catch (e) { /* no ceiling known */ }
    notes.push(tr(info.ext === 'mp3' ? 'note_mp3_reencoded' : 'note_aac_reencoded', { kbps }));
    return { blob: out, ext: info.ext, intro: introInfo(intro, info.rate, a, lang, every) };
}

function diffRows(it, mk) {
    const rows = [];
    const beforeItems = (it.verdict ? it.verdict.items : (it.report ? it.report.items : []));
    const findBefore = (field) => {
        const b = beforeItems.find((i) => i.key === field || (field === 'Iptc4xmpExt:DigitalSourceType' && /^dst_/.test(i.code) && i.source !== 'C2PA'));
        return b ? b.value : '';
    };
    mk.writes.forEach((w) => {
        rows.push({ where: w.where, what: tr('field_' + w.field.replace(/[^a-z0-9]/gi, '_').toLowerCase()) !== 'field_' + w.field.replace(/[^a-z0-9]/gi, '_').toLowerCase() ? tr('field_' + w.field.replace(/[^a-z0-9]/gi, '_').toLowerCase()) : w.field, before: findBefore(w.field), after: w.value });
    });
    if (mk.visible) rows.unshift({ where: tr('where_pixels'), what: tr('what_visible'), before: '', after: tr('visible_desc', { x: mk.visible.rect.x, y: mk.visible.rect.y, w: mk.visible.rect.w, h: mk.visible.rect.h }) });
    if (mk.intro) rows.unshift({ where: tr('where_sound'), what: tr('what_intro'), before: '', after: tr('intro_desc', mk.intro) });
    return rows;
}

async function run() {
    if (S.running) return;
    const targets = S.files.filter((f) => f.markable);
    if (!targets.length) return;
    funnel('run_click', { n: targets.length, preset: S.preset });
    // per FILE, repeats of the same file today free; a batch over the remainder is refused whole
    const L = window.__toolLimit;
    let token = null;
    if (L) {
        const keys = L.fileKeys(targets.map((f) => f.file));
        token = L.begin(SLUG, keys);
        if (!token) { ae('daily_limit', { n: targets.length }); diag('daily_limit', { n: targets.length }); return; }
    }
    S.running = true; S.token = token;
    runBtn.disabled = true;
    const bar = $('#ailProgress'), barFill = bar.querySelector('i'), barText = $('#ailProgressText');
    bar.hidden = false;
    let ok = 0;
    for (let k = 0; k < targets.length; k++) {
        const it = targets[k];
        if (it.result && it.result.thumbUrl) URL.revokeObjectURL(it.result.thumbUrl);
        it.result = null;
        barText.textContent = tr('working', { i: k + 1, n: targets.length, name: it.file.name });
        const step = (p) => { barFill.style.width = Math.round(((k + Math.max(0, Math.min(1, p || 0))) / targets.length) * 100) + '%'; };
        step(0);
        try {
            if (window.__stage) window.__stage('processing', { kind: it.kind });
            const mk = await markOne(it, step);
            it.result = { blob: mk.blob, name: mk.name, writes: mk.writes, notes: mk.notes, rows: null, kind: S.kind };
            if (it.kind === 'image' && mk.blob.type.indexOf('image/') === 0 && !/heic/.test(mk.blob.type)) it.result.previewUrl = URL.createObjectURL(mk.blob);
            it.result.rows = diffRows(it, mk);
            // the oracle is the reader, not the writer: what does the file say now?
            const rep = await window.AiLabelInspect.inspect(mk.blob, mk.name);
            const v = window.AiLabelInspect.verdict(rep);
            const want = KINDS[S.kind].dst;
            it.result.verified = rep.items.some((i) => i.code === 'dst_' + want);
            it.result.report = rep; it.result.verdict = v;
            ok++;
            ae('marked', { kind: it.kind, ext: it.ext, preset: S.preset, k: S.kind, vis: mk.visible ? 1 : 0, aud: mk.intro ? 1 : 0, lane: it.lane || '', verified: it.result.verified ? 1 : 0, grow: Math.round(mk.blob.size / Math.max(1, it.file.size) * 100) });
            if (!it.result.verified) diag('mark_unverified', { kind: it.kind, ext: it.ext });
        } catch (e) {
            it.result = { error: (e && e.userText) || tr('err_mark') };
            diag('mark_failed', { kind: it.kind, ext: it.ext, err: errCode(e), refused: e && e.userText ? 1 : 0 });
            if (!(e && e.userText)) window.__handleError && window.__handleError(e, 'ai_label_mark', { say: tr('err_mark') });
        }
        step(1);
        renderFile(it);
    }
    bar.hidden = true;
    S.running = false;
    renderAll();
    if (window.__aeToolComplete && ok) { try { window.__aeToolComplete({ n: ok }); } catch (e) { /* telemetry */ } }
    if (token && token.awaitDelivery) {
        const first = targets.find((f) => f.result && f.result.blob);
        if (first) token.awaitDelivery({ blob: first.result.blob, name: first.result.name });
    }
    if (!ok && token) { token.abort(); S.token = null; }
    if (ok) { showCaption(); funnel('marked', { n: ok, of: targets.length }); }
    if (window.__announce) window.__announce(tr('done_n', { n: ok, total: targets.length }));
    const firstCard = list.querySelector('.ail-result');
    if (firstCard && firstCard.scrollIntoView) firstCard.scrollIntoView({ block: 'nearest' });
}

/* The words that go next to the file where it is published: platforms ask the author to say it
   (Meta, TikTok, YouTube toggles), and Korea's rule for a machine-only mark is a notice to the
   person. The Korean sentence rides along on the Korea preset whatever the page language. */
function captionText() {
    let t = tr('caption_' + (S.kind === 'modified' ? 'modified' : 'generated'));
    if (S.aiSystem) t += ' ' + tr('caption_system', { system: S.aiSystem });
    if (S.preset === 'kr' && LOCALE.slice(0, 2) !== 'ko') t += ' / ' + (S.kind === 'modified' ? '이 콘텐츠는 AI로 수정되었습니다.' : '이 콘텐츠는 AI로 생성되었습니다.');
    if (S.preset === 'cn' && LOCALE.slice(0, 2) !== 'zh') t += ' / ' + (S.kind === 'modified' ? '本内容经人工智能合成。' : '本内容由人工智能生成。');
    return t;
}
function showCaption() {
    $('#ailCaptionText').textContent = captionText();
    $('#ailCaption').hidden = false;
}

/* The check as plain text — for a moderator's ticket, a newsroom chat or a reply to «is this
   AI?»: the verdict, the C2PA answer and every claim, in the page's language. */
function reportText(it) {
    const v = it.verdict, lines = [];
    if (!v) return '';
    lines.push(it.file.name + ' — ' + tr('v_' + v.level + '_title'));
    if (it.c2pa) {
        const c = it.c2pa;
        lines.push(tr('c2pa_title') + ': ' + tr('c2pa_' + c2paKey(c)));
        if (c.commonName || c.issuer) lines.push('  ' + tr('c2pa_signer') + ': ' + [c.commonName, c.issuer].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(' · '));
        if (c.generator) lines.push('  ' + tr('c2pa_generator') + ': ' + c.generator);
        if (c.time) lines.push('  ' + tr('c2pa_time') + ': ' + fmtDate(c.time));
        c.actions.forEach((a) => lines.push('  ' + tr('c2pa_action') + ': ' + actionText(a)));
        if (c.failures.length) lines.push('  ' + tr('c2pa_failures') + ': ' + c.failures.map(failText).join('; '));
    } else if (it.c2paState === 'bad') lines.push(tr('c2pa_bad', { code: it.c2paCode || '?' }));
    v.items.filter((i) => i.code !== 'c2pa_present' && i.source !== 'C2PA').forEach((i) => lines.push('• ' + itemText(i) + ' [' + i.source + ' · ' + i.key + ']'));
    v.contradictions.forEach((c) => lines.push('! ' + tr('contra_' + c.code, { values: (c.values || []).join(', ') })));
    if (v.level === 'none' || v.level === 'capture' || v.level === 'info') lines.push(tr('note_none'));
    return lines.join('\n');
}
async function copyReport(id) {
    const it = S.files.find((f) => f.id === id);
    if (!it) return;
    const ok = window.__copyText ? await window.__copyText(reportText(it)) : false;
    if (window.__toast) window.__toast(tr(ok ? 'copied' : 'copy_failed'), ok ? 'success' : 'error');
    ae('report_copy', { ok: ok ? 1 : 0, level: it.verdict ? it.verdict.level : '' });
}

/* billing on delivery: the files handed out are the ones counted */
function settleDelivered(files) {
    const L = window.__toolLimit, token = S.token;
    if (!token) return;
    const all = S.files.filter((f) => f.result && f.result.blob);
    const delivered = all.filter((f) => f.result.delivered);
    if (delivered.length === all.length) { token.commit(); S.token = null; return; }
    if (L && L.record && files && files.length) {
        // part of a batch taken: count those now, keep the token for the rest
        L.record(SLUG, L.fileKeys(files.map((f) => f.file)));
    }
}

async function downloadOne(id) {
    const it = S.files.find((f) => f.id === id);
    if (!it || !it.result || !it.result.blob) return;
    const ok = await window.__downloadFile(it.result.blob, it.result.name);
    if (ok !== false) { it.result.delivered = true; settleDelivered([it]); ae('download', { n: 1 }); funnel('downloaded', { n: 1 }); }
}

async function downloadAll() {
    const ready = S.files.filter((f) => f.result && f.result.blob);
    if (!ready.length) return;
    if (!window.__batchDeliver) {
        if (window.__BATCH_DELIVER_SRC && window.__loadScriptsOnce) await window.__loadScriptsOnce([window.__BATCH_DELIVER_SRC], 'batch-deliver');
    }
    if (!window.__batchDeliver) { for (const f of ready) await downloadOne(f.id); return; }
    const res = await window.__batchDeliver.deliver(ready.map((f) => ({ name: f.result.name, blob: f.result.blob })), { zipName: 'ai-label.zip', slug: SLUG });
    if (res && (res.via === 'folder' || res.via === 'archive' || res.written)) {
        ready.forEach((f) => { f.result.delivered = true; });
        settleDelivered(ready);
        ae('download', { n: ready.length, via: res.via || '' });
        funnel('downloaded', { n: ready.length });
    }
}

function precheck() {
    const L = window.__toolLimit;
    if (!L || !L.precheck || S.mode !== 'mark') return;
    try { L.precheck(SLUG, S.files.filter((f) => f.kind !== 'other').map((f) => f.file)); } catch (e) { /* the press still asks begin() */ }
}

/* ───────────── wire up ───────────── */

function init() {
    if (!root || !window.AiLabelInspect || !window.__aiMark) return;
    settingsLoad();
    const lang = $('#ailLang');
    if (lang && !lang.options.length) {
        const o = document.createElement('option'); o.value = 'auto'; o.textContent = tr('lang_auto', { lang: disclaimerLang('auto', LOCALE).toUpperCase() }); lang.appendChild(o);
        DISCLAIMER_LANGS.forEach((l) => { const x = document.createElement('option'); x.value = l; x.textContent = (W.langNames && W.langNames[l]) || l; lang.appendChild(x); });
    }
    $$('[data-ail-mode]').forEach((b) => b.addEventListener('click', () => setMode(b.dataset.ailMode)));
    bindRail();
    runBtn.addEventListener('click', run);
    $('#ailDlAll').addEventListener('click', downloadAll);
    $('#ailCaptionCopy').addEventListener('click', async () => {
        const ok = window.__copyText ? await window.__copyText(captionText()) : false;
        ae('caption_copy', { ok: ok ? 1 : 0, preset: S.preset });
        if (window.__toast) window.__toast(tr(ok ? 'copied' : 'copy_failed'), ok ? 'success' : 'error');
    });
    const clearAll = (fromZone) => {
        S.files.forEach((f) => { if (f.thumbUrl) URL.revokeObjectURL(f.thumbUrl); if (f.heicThumbUrl) URL.revokeObjectURL(f.heicThumbUrl); if (f.result && f.result.previewUrl) URL.revokeObjectURL(f.result.previewUrl); });
        S.files = []; root.classList.remove('has-files'); $('#ailWork').hidden = true;
        if (!fromZone && zone.__uploaderRestore) zone.__uploaderRestore(true);
        if (S.token) { S.token.abort(); S.token = null; }
        renderAll();
    };
    $('#ailClear').addEventListener('click', () => clearAll(false));
    zone.addEventListener('tool-uploader:clear', () => clearAll(true));
    $('#ailAdd').addEventListener('click', () => { if (zone.__uploaderOpen) zone.__uploaderOpen(); });
    list.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-ail-rm]'); if (rm) { removeFile(Number(rm.dataset.ailRm)); return; }
        const dl = e.target.closest('[data-ail-dl]'); if (dl) downloadOne(Number(dl.dataset.ailDl));
        const cp = e.target.closest('[data-ail-copy]'); if (cp) copyReport(Number(cp.dataset.ailCopy));
    });
    const onPick = (f, d) => { const files = (d && d.files && d.files.length) ? d.files : (f ? [f] : []); addFiles(files); if (zone.__uploaderReset) zone.__uploaderReset(); };
    if (window.__bindUploaderFile) window.__bindUploaderFile(zone, onPick);
    else zone.addEventListener('tool-uploader:file', (e) => onPick(e.detail && e.detail.file, e.detail));
    let m = 'mark';
    try { m = new URL(location.href).searchParams.get('mode') === 'check' ? 'check' : 'mark'; } catch (e) { /* default */ }
    setMode(m);
    document.addEventListener('tier-bootstrap:ready', precheck);
    root.dataset.ready = '1';
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
